<template>
  <component
    :is="component"
    :class="
      twMerge(
        'app-button inline-flex flex-shrink-0 items-center justify-center gap-2.5 rounded-button text-center font-button font-bold transition-all duration-300 ease-in-out',
        classes,
      )
    "
    v-bind="$attrs"
  >
    <!-- Dynamic left icon (radio buttons) -->
    <component
      :is="getIconComponent()"
      v-if="getIconPosition() === 'left' && getIconComponent()"
      class="shrink-0"
    />

    <!-- Manual left icon slot -->
    <slot name="icon-left" />

    <!-- Main content slot -->
    <slot />

    <!-- Manual right icon slot -->
    <slot name="icon-right" />

    <!-- Dynamic right icon (filters, close, generate, etc.) -->
    <component
      :is="getIconComponent()"
      v-if="getIconPosition() === 'right' && getIconComponent()"
      class="shrink-0"
    />
  </component>
</template>

<script setup lang="ts">
import IconRadio from '@/components/icons/IconRadio.vue'
import IconRadioFilled from '@/components/icons/IconRadioFilled.vue'
import IconArrowDown from '@/components/icons/IconArrowDown.vue'
import IconArrowUp from '@/components/icons/IconArrowUp.vue'
import IconDoubleArrowDown from '@/components/icons/IconDoubleArrowDown.vue'
import IconDoubleArrowUp from '@/components/icons/IconDoubleArrowUp.vue'
import IconDoubleArrowLeft from '@/components/icons/IconDoubleArrowLeft.vue'
import IconDoubleArrowRight from '@/components/icons/IconDoubleArrowRight.vue'
import IconArrowLeft from '@/components/icons/IconArrowLeft.vue'
import IconArrowRight from '@/components/icons/IconSingleArrowRight.vue'
import IconClose from '@/components/icons/IconClose.vue'
import IconGenerate from '@/components/icons/IconGenerate.vue'
import { twMerge } from 'tailwind-merge'

interface AppButtonProps {
  as?: 'button' | 'a' | 'nuxt-link'
  variant?:
    | 'DEFAULT'
    | 'green'
    | 'purple'
    | 'blue'
    | 'peach'
    | 'pink'
    | 'rosa'
    | 'lila'
    | 'ljusrosa'
    | 'ljuslila'
  outlined?: boolean
  size?: 'md' | 'lg' | 'desktop' | 'mobile'
  disabled?: boolean
  // New simplified props
  type?: 'button' | 'radio' | 'toggle' | 'close' | 'generate' | 'submit'
  checked?: boolean
  expanded?: boolean
  position?: 'left' | 'right' | 'none'
  arrowType?: 'single' | 'double'
}

const props = withDefaults(defineProps<AppButtonProps>(), {
  as: 'button',
  variant: 'DEFAULT',
  outlined: false,
  size: 'md',
  disabled: false,
  type: 'button',
  checked: false,
  expanded: false,
  position: 'none',
  arrowType: 'single',
})

// Computed component type
const component = computed(() => {
  return props.as === 'nuxt-link' ? resolveComponent('NuxtLink') : props.as
})

// Get icon component based on button type and state
function getIconComponent() {
  // Handle arrow icons when position is set
  if (props.position !== 'none' && props.type === 'button') {
    if (props.arrowType === 'double') {
      switch (props.position) {
        case 'right':
          return IconDoubleArrowRight
        case 'left':
          return IconDoubleArrowLeft
        default:
          return null
      }
    } else {
      // Single arrows - we'll need to create these or use existing ones
      switch (props.position) {
        case 'right':
          return IconArrowRight
        case 'left':
          return IconArrowLeft
        default:
          return null
      }
    }
  }

  // Handle interactive button types
  switch (props.type) {
    case 'radio':
      return props.checked ? IconRadioFilled : IconRadio
    case 'toggle':
      if (props.arrowType === 'double') {
        return props.expanded ? IconDoubleArrowUp : IconDoubleArrowDown
      } else {
        return props.expanded ? IconArrowUp : IconArrowDown
      }
    case 'close':
      return IconClose
    case 'generate':
      return IconGenerate
    default:
      return null
  }
}

// Get icon position based on button type or manual position
function getIconPosition(): 'left' | 'right' | 'none' {
  // If position is manually set, use it
  if (props.position !== 'none') {
    return props.position
  }

  // Otherwise use default positions for types
  switch (props.type) {
    case 'radio':
      return 'left'
    case 'toggle':
    case 'close':
    case 'generate':
      return 'right'
    default:
      return 'none'
  }
}

// Get classes for interactive button types
function getInteractiveButtonClasses(): string[] {
  const sizeClasses = getSizeClasses(props.size)
  const disabledClasses = props.disabled ? 'opacity-50 cursor-not-allowed' : ''

  switch (props.type) {
    case 'radio':
      if (props.checked) {
        // Checked state: variant background, white text
        return [
          ...sizeClasses,
          disabledClasses,
          props.variant === 'rosa' || props.variant === 'ljusrosa'
            ? 'bg-pink-dark-base text-white hover:bg-pink-darker'
            : 'bg-purple-dark-base text-white hover:bg-purple-darker',
        ]
      } else {
        // Unchecked state: white background, variant text
        return [
          ...sizeClasses,
          disabledClasses,
          props.variant === 'rosa' || props.variant === 'ljusrosa'
            ? 'bg-white text-pink-dark-base hover:bg-pink-lightest'
            : 'bg-white text-purple-dark-base hover:bg-purple-lightest',
        ]
      }

    case 'toggle':
      // Toggle buttons: white background, variant text
      return [
        ...sizeClasses,
        disabledClasses,
        props.variant === 'rosa' || props.variant === 'ljusrosa'
          ? 'bg-white text-pink-dark-base hover:bg-pink-lightest'
          : 'bg-white text-purple-dark-base hover:bg-purple-lightest',
      ]

    case 'close':
      // Close button: white background, variant text
      return [
        ...sizeClasses,
        disabledClasses,
        props.variant === 'rosa' || props.variant === 'ljusrosa'
          ? 'bg-white text-pink-dark-base hover:bg-pink-lightest'
          : 'bg-white text-purple-dark-base hover:bg-purple-lightest',
      ]

    case 'generate':
      // Generate button: light variant background, variant text
      return [
        ...sizeClasses,
        disabledClasses,
        props.variant === 'rosa' || props.variant === 'ljusrosa'
          ? 'bg-pink-lightest text-pink-dark-base hover:bg-pink-lighter'
          : 'bg-purple-lightest text-purple-dark-base hover:bg-purple-lighter',
      ]

    default:
      return [...sizeClasses, disabledClasses]
  }
}

// Get size-specific classes
function getSizeClasses(size: string): string[] {
  if (size === 'desktop' || size === 'lg') {
    // Desktop size with responsive mobile fallback
    return [
      'h-button-mobile', // Mobile first
      'w-button-mobile', // Mobile first
      'p-button-mobile', // Mobile first
      'text-button-mobile', // Mobile first
      'leading-button-mobile', // Mobile first
      // Desktop overrides
      'sm:h-button-desktop',
      'sm:w-auto', // Remove fixed width on desktop
      'sm:p-button-desktop',
      'sm:text-button-desktop',
      'sm:leading-button-desktop',
    ]
  }
  return [
    'h-button-mobile',
    'w-button-mobile',
    'p-button-mobile',
    'text-button-mobile',
    'leading-button-mobile',
  ]
}

// Get classes for regular button types
function getRegularButtonClasses(): string[] {
  const sizeClasses = getSizeClasses(props.size)
  const disabledClasses = props.disabled ? 'opacity-50 cursor-not-allowed' : ''

  const variantClasses = props.outlined
    ? {
        DEFAULT:
          'border-green-DEFAULT text-green-DEFAULT hover:bg-green-DEFAULT-dark hover:border-green-dark hover:text-white',
        green:
          'border-green text-green hover:bg-green-dark hover:border-green-dark hover:text-white',
        purple:
          'border-purple text-purple hover:bg-purple-dark hover:border-purple-dark hover:text-white',
        blue: 'border-blue text-blue hover:bg-blue hover:border-blue hover:text-white',
        peach:
          'border-peach text-peach hover_bg-peach-dark hover_border-peach-dark hover_text-white',
        pink: 'border-pink text-pink hover_bg-pink hover_border-pink hover_text-white',
        // New design system variants
        rosa: 'border-pink-dark-base text-pink-dark-base hover:bg-pink-dark-base hover:text-white',
        lila: 'border-purple-dark-base text-purple-dark-base hover:bg-purple-dark-base hover:text-white',
        ljusrosa:
          'border-pink-lightest text-pink-dark-base hover:bg-pink-lighter hover:text-pink-dark-base',
        ljuslila:
          'border-purple-lightest text-purple-dark-base hover:bg-purple-lighter hover:text-purple-dark-base',
      }[props.variant] || ''
    : {
        DEFAULT: 'bg-green-DEFAULT hover_bg-green-DEFAULT-dark text-white',
        green: 'bg-green hover_bg-green-dark text-white',
        purple: 'bg-purple hover_bg-purple-dark text-white',
        blue: 'bg-blue hover_bg-blue text-white',
        peach: 'bg-peach hover_bg-peach-dark text-white',
        pink: 'bg-pink hover_bg-pink-dark text-white',
        // New design system variants
        rosa: 'bg-pink-dark-base hover:bg-pink-darker text-white',
        lila: 'bg-purple-dark-base hover:bg-purple-darker text-white',
        ljusrosa: 'bg-pink-lightest hover:bg-pink-lighter text-pink-dark-base',
        ljuslila: 'bg-purple-lightest hover:bg-purple-lighter text-purple-dark-base',
      }[props.variant] || ''

  return [
    ...sizeClasses,
    disabledClasses,
    ...(props.outlined ? ['bg-transparent border-2', variantClasses] : [variantClasses]),
  ]
}

// Computed button classes
const classes = computed(() => {
  // Handle interactive button types with automatic styling
  if (props.type !== 'button') {
    return getInteractiveButtonClasses()
  }
  // Regular button types with unified sizing
  return getRegularButtonClasses()
})
</script>

<style lang="postcss" scoped>
.app-button {
  /* Remove fixed font size - let Tailwind classes handle it */
  @apply hover:scale-[1.05];

  /* Ensure proper font rendering */
  font-family: 'Avenir LT Std', sans-serif;
  font-weight: 700;

  /* Smooth transitions */
  transition: all 0.3s ease-in-out;

  /* Remove default focus outline */
  &:focus {
    outline: none;
  }

  /* Disabled state */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
}
</style>
